import { AltiusRes, IPaginationResponse, PostDealCharacteristicsDataBody, TaskPreview } from "store/types";
import {
  ClientDetail,
  IAvailableInvestor,
  IClientCharacteristicsForm,
  IClientCharacteristicValue,
  IClientWithInvestors,
  IClientEntityAllocation,
  IClientReportingScope,
  IClientTaskFilters,
  ICreateAllocationPayload,
  ICreateClientPayload,
  ISaveCharacteristicValuesPayload,
  IUpdateClientPayload
} from "store/types/client";
import { IClientOverviewDeals } from "store/types/client/client-overview";
import { IClient, IClientFilters } from "store/types/user-management";

import { ClientAction } from "../actions";
import { AsyncActions } from "../base";

export interface IClientActions {
  //  Clients CRUD ----
  getClientsAction: (filters: IClientFilters) => Promise<IPaginationResponse<IClient>>;
  createClientAction: (payload: ICreateClientPayload) => Promise<ClientDetail>;
  getClientDetailAction: (clientId: number) => Promise<ClientDetail>;
  updateClientAction: (clientId: number, payload: IUpdateClientPayload) => Promise<ClientDetail>;
  deleteClientAction: (clientId: number) => Promise<void>;

  // Characteristic values ----
  getClientCharacteristicValuesAction: (clientId: number) => Promise<IClientCharacteristicValue[]>;
  saveClientCharacteristicValuesAction: (
    clientId: number,
    payload: ISaveCharacteristicValuesPayload
  ) => Promise<IClientCharacteristicValue[]>;
  // Full characteristics form for the client (Manage drawer). Trimmed shape vs the deal endpoint.
  getClientCharacteristicsDataAction: (clientId: number) => Promise<IClientCharacteristicsForm>;
  // Single save endpoint (analogous to the deal's storeCharacteristicsData POST /characteristics/data).
  // Used by BOTH the drawer and the system-char pills. `characteristics` is optional (pills send values only).
  // Refreshes the client detail so the bar reflects the change.
  postClientCharacteristicsDataAction: (
    clientId: number,
    body: {
      characteristics?: PostDealCharacteristicsDataBody["characteristics"];
      characteristic_data: PostDealCharacteristicsDataBody["characteristic_data"];
    }
  ) => Promise<void>;

  // Allocations / Entities ----
  getAllocations: (clientId: number, without_count?: boolean) => Promise<IClientEntityAllocation[]>;
  getAvailableInvestors: (clientId: number) => Promise<IAvailableInvestor[]>;
  createAllocation: (clientId: number, payload: ICreateAllocationPayload) => Promise<IClientEntityAllocation>;
  deleteAllocation: (clientId: number, allocationId: number) => Promise<void>;
  getClientOverviewDealsAction: (clientId: number) => Promise<IClientOverviewDeals>;

  // Clients with their investors (Report Generator export-to-client). Gated by enable_client_crm.
  getClientsWithInvestorsAction: () => Promise<IClientWithInvestors[]>;
  // Upload generated report PDFs to client workspaces. Gated by show_ai_report_generator && enable_client_crm.
  uploadClientReportFilesAction: (
    items: { file: File; clientId: number; investorId?: number | null }[]
  ) => Promise<void>;

  // Report reporting-scope ----
  getClientReportingScopeAction: (clientId: number) => Promise<IClientReportingScope>;

  // Tasks ----
  getClientTasksAction: (clientId: number, filters?: IClientTaskFilters) => Promise<TaskPreview[]>;
}

export class ClientActions extends AsyncActions implements IClientActions {
  public getClientsAction: IClientActions["getClientsAction"] = async (filters) => {
    // Strip undefined and empty values so the API receives a clean query string.
    const cleanedFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""));

    try {
      const {
        data: { data }
      } = await this.api.get<AltiusRes<IPaginationResponse<IClient>>>({
        url: "/api/v0.0.3/clients",
        signal: this.addProcess("getClientsAction").signal,
        params: cleanedFilters
      });
      this.removeProcess("getClientsAction");
      return data;
    } catch (error) {
      this.removeProcess("getClientsAction");
      throw error;
    }
  };

  public createClientAction: IClientActions["createClientAction"] = async (payload) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<ClientDetail>, ICreateClientPayload>({
      url: "/api/v0.0.3/clients",
      data: payload
    });
    return data;
  };

  public getClientDetailAction: IClientActions["getClientDetailAction"] = async (clientId) => {
    // Reset any stale client before loading so sub-pages never read the previous client
    // (and so a failed load leaves the slice empty rather than showing old data).
    this.dispatch({ type: ClientAction.ClearClient, payload: null });
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ClientDetail>>({
      url: `/api/v0.0.3/clients/${clientId}`,
      signal: this.addProcess("getClientDetailAction").signal
    });
    this.removeProcess("getClientDetailAction");
    this.dispatch({ type: ClientAction.FetchClientSuccess, payload: data });
    return data;
  };

  public updateClientAction: IClientActions["updateClientAction"] = async (clientId, payload) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<ClientDetail>, IUpdateClientPayload>({
      url: `/api/v0.0.3/clients/${clientId}`,
      signal: this.addProcess("updateClientAction").signal,
      data: payload
    });
    this.removeProcess("updateClientAction");
    return data;
  };

  public deleteClientAction: IClientActions["deleteClientAction"] = async (clientId) => {
    await this.api.delete<AltiusRes<null>>({
      url: `/api/v0.0.3/clients/${clientId}`,
      signal: this.addProcess("deleteClientAction").signal
    });
    this.removeProcess("deleteClientAction");
  };

  public getClientCharacteristicValuesAction: IClientActions["getClientCharacteristicValuesAction"] = async (
    clientId
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientCharacteristicValue[]>>({
      url: `/api/v0.0.3/clients/${clientId}/characteristic-values`,
      signal: this.addProcess("getClientCharacteristicValuesAction").signal
    });
    this.removeProcess("getClientCharacteristicValuesAction");
    return data;
  };

  public saveClientCharacteristicValuesAction: IClientActions["saveClientCharacteristicValuesAction"] = async (
    clientId,
    payload
  ) => {
    const {
      data: { data }
    } = await this.api.put<AltiusRes<IClientCharacteristicValue[]>, ISaveCharacteristicValuesPayload>({
      url: `/api/v0.0.3/clients/${clientId}/characteristic-values`,
      signal: this.addProcess("saveClientCharacteristicValuesAction").signal,
      data: payload
    });
    this.removeProcess("saveClientCharacteristicValuesAction");
    return data;
  };

  // Refresh client detail in place — dispatch only FetchClientSuccess (no ClearClient) to avoid flashing the card.
  private refreshClientDetail = async (clientId: number) => {
    const {
      data: { data: client }
    } = await this.api.get<AltiusRes<ClientDetail>>({ url: `/api/v0.0.3/clients/${clientId}` });
    this.dispatch({ type: ClientAction.FetchClientSuccess, payload: client });
  };

  public getClientCharacteristicsDataAction: IClientActions["getClientCharacteristicsDataAction"] = async (
    clientId
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientCharacteristicsForm>>({
      url: `/api/v0.0.3/clients/${clientId}/characteristics/data`,
      signal: this.addProcess("getClientCharacteristicsDataAction").signal
    });
    this.removeProcess("getClientCharacteristicsDataAction");
    return data;
  };

  public postClientCharacteristicsDataAction: IClientActions["postClientCharacteristicsDataAction"] = async (
    clientId,
    body
  ) => {
    await this.api.post<AltiusRes<{ message: string }>>({
      url: `/api/v0.0.3/clients/${clientId}/characteristics/data`,
      data: body
    });
    await this.refreshClientDetail(clientId);
  };

  public getClientsWithInvestorsAction: IClientActions["getClientsWithInvestorsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientWithInvestors[]>>({
      url: "/api/v0.0.3/clients/with-investors",
      signal: this.addProcess("getClientsWithInvestorsAction").signal
    });
    this.removeProcess("getClientsWithInvestorsAction");
    return data;
  };

  public uploadClientReportFilesAction: IClientActions["uploadClientReportFilesAction"] = async (items) => {
    // Indexed multipart array: files[i][file|client_id|investor_id]. Omit investor_id entirely when absent
    // (an empty string fails the backend `integer` rule).
    const fd = new FormData();
    items.forEach((item, i) => {
      fd.append(`files[${i}][file]`, item.file);
      fd.append(`files[${i}][client_id]`, String(item.clientId));
      if (item.investorId != null) fd.append(`files[${i}][investor_id]`, String(item.investorId));
    });
    await this.api.post({
      url: "/api/v0.0.3/report-generator/client-files",
      data: fd,
      headers: { "Content-Type": "multipart/form-data" }
    });
  };

  public getAllocations: IClientActions["getAllocations"] = async (clientId, without_count = false) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientEntityAllocation[]>>({
      url: `/api/v0.0.3/clients/${clientId}/allocations`,
      signal: this.addProcess("getAllocations").signal,
      params: { without_count }
    });
    this.removeProcess("getAllocations");
    return data;
  };

  public getAvailableInvestors: IClientActions["getAvailableInvestors"] = async (clientId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IAvailableInvestor[]>>({
      url: `/api/v0.0.3/clients/${clientId}/available-investors`,
      signal: this.addProcess("getAvailableInvestors").signal
    });
    this.removeProcess("getAvailableInvestors");
    return data;
  };

  public createAllocation: IClientActions["createAllocation"] = async (clientId, payload) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IClientEntityAllocation>, ICreateAllocationPayload>({
      url: `/api/v0.0.3/clients/${clientId}/allocations`,
      signal: this.addProcess("createAllocation").signal,
      data: payload
    });
    this.removeProcess("createAllocation");
    return data;
  };

  public deleteAllocation: IClientActions["deleteAllocation"] = async (clientId, allocationId) => {
    await this.api.delete<AltiusRes<null>>({
      url: `/api/v0.0.3/clients/${clientId}/allocations/${allocationId}`,
      signal: this.addProcess("deleteAllocation").signal
    });
    this.removeProcess("deleteAllocation");
  };

  public getClientOverviewDealsAction: IClientActions["getClientOverviewDealsAction"] = async (clientId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientOverviewDeals>>({
      url: `/api/v0.0.3/clients/${clientId}/deals`,
      signal: this.addProcess("getClientOverviewDealsAction").signal
    });
    this.removeProcess("getClientOverviewDealsAction");
    return data;
  };

  public getClientReportingScopeAction: IClientActions["getClientReportingScopeAction"] = async (clientId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientReportingScope>>({
      url: `/api/v0.0.3/clients/${clientId}/reporting-scope`,
      signal: this.addProcess("getClientReportingScopeAction").signal
    });
    this.removeProcess("getClientReportingScopeAction");
    return data;
  };

  public getClientTasksAction: IClientActions["getClientTasksAction"] = async (clientId, filters) => {
    const cleanedFilters = filters
      ? Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""))
      : undefined;

    const {
      data: { data }
    } = await this.api.get<AltiusRes<TaskPreview[]>>({
      url: `/api/v0.0.3/clients/${clientId}/tasks`,
      signal: this.addProcess("getClientTasksAction").signal,
      params: cleanedFilters
    });
    this.removeProcess("getClientTasksAction");
    return data;
  };
}
